import { useCallback, useEffect, useRef, useState } from "react";
import { getUserPhotoService } from "../services/UserInfoService";
import {
  DEFAULT_PROFILE_IMAGE,
  getProfileImageFromUser,
  normalizeImageSrc,
} from "../utils/normalizeImageSrc";

/**
 * Resuelve la foto de perfil de las sugerencias de arroba (@) del compositor.
 *
 * El endpoint de sugerencias (/api/arrobar/usuario) no devuelve la foto de forma
 * fiable, así que se completa por usuario — mismo enfoque que usa
 * AutoCompleteSearchEvents para el buscador. `getUserPhotoService` reutiliza la
 * caché y la deduplicación en vuelo de `getUserInfoService`, por lo que pedir la
 * foto de varios usuarios no genera ráfagas de peticiones.
 *
 * Devuelve una función `getMentionPhoto(user)` lista para usar en un `src`.
 */
export const useMentionSuggestionPhotos = (suggestions) => {
  const [photos, setPhotos] = useState({});
  // Evita volver a pedir la misma foto y que el efecto se re-dispare al guardarla.
  const requestedRef = useRef(new Set());

  useEffect(() => {
    let cancelled = false;

    (Array.isArray(suggestions) ? suggestions : []).forEach((user) => {
      const id = user?.idUsuario;
      if (!id || requestedRef.current.has(id)) return;
      requestedRef.current.add(id);

      const fallback = getProfileImageFromUser(user) || DEFAULT_PROFILE_IMAGE;

      getUserPhotoService({ IdUsuario: id })
        .then(({ content }) => {
          if (cancelled) return;
          setPhotos((current) => ({
            ...current,
            [id]: normalizeImageSrc(content) || fallback,
          }));
        })
        .catch(() => {
          if (cancelled) return;
          setPhotos((current) => ({ ...current, [id]: fallback }));
        });
    });

    return () => {
      cancelled = true;
    };
  }, [suggestions]);

  return useCallback(
    (user) =>
      photos[user?.idUsuario] ||
      getProfileImageFromUser(user) ||
      DEFAULT_PROFILE_IMAGE,
    [photos],
  );
};

export { DEFAULT_PROFILE_IMAGE };
